import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase, supabaseInitError } from '@/integrations/supabase/client';
import { reportError } from '@/lib/error-tracking';
import { isChunkLoadError, shouldAutoReload, clearReloadState } from '@/lib/chunk-reload';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    if (isChunkLoadError(error)) {
      // Auto-reload to pick up the new deployment, but cap retries via
      // the SHARED chunk-reload counter (src/lib/chunk-reload.ts) so the
      // global unhandledrejection listener and this boundary cooperate
      // on a single attempt budget. See follow-up to #1313 — before the
      // shared module, both handlers wrote to the same sessionStorage
      // key with incompatible formats and stomped on each other forever.
      if (!shouldAutoReload()) {
        // Hit the cap — surface the persistent UI so the user gets an
        // explicit recovery path instead of an infinite reload loop.
        return { hasError: true, error, isChunkError: true };
      }
      window.location.reload();
      // Return non-error state while the reload happens
      return { hasError: false, error: null, isChunkError: true };
    }

    // Clear the reload flag on any successful non-chunk error render after
    // a previous chunk reload (counter resets on a healthy render path).
    clearReloadState();
    return { hasError: true, error, isChunkError: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Skipped intentionally — chunk errors are deployment artifacts, not
    // app bugs. Reporting them to Sentry every time we see one would bury
    // real errors. See issue #1313.
    if (isChunkLoadError(error)) return;

    // Report to error tracking
    reportError(error, {
      component: 'ErrorBoundary',
      action: 'componentDidCatch',
      componentStack: errorInfo.componentStack || undefined,
      route: window.location.pathname,
    });

    // Fire-and-forget: report to System Health dashboard (skip if Supabase is unavailable)
    if (supabaseInitError) return;
    try {
      supabase.functions.invoke('log-frontend-error', {
        body: {
          error_message: error.message,
          error_stack: error.stack || null,
          context: {
            componentStack: errorInfo.componentStack || null,
            route: window.location.pathname,
            fallbackTitle: this.props.fallbackTitle || null,
          },
        },
      }).catch(() => { /* swallow */ });
    } catch {
      // Never let reporting interfere with fallback UI
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  handleRefresh = () => {
    // Intentionally clear the counter before reloading — a deliberate user
    // action after reading the "new version" UI deserves a fresh 3-attempt
    // window in case the CDN stabilized between the auto-reload storm and now.
    clearReloadState();
    // Cache-bust the URL so the browser fetches fresh index.html even when
    // an upstream proxy / SW served a stale copy that referenced dead chunks.
    // Vercel sends `Cache-Control: no-cache, must-revalidate` on the HTML
    // and there's no SW today, but the cache-buster makes this resilient to
    // either landing later (and to misbehaving corporate proxies).
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', String(Date.now()));
    window.location.replace(url.toString());
  };

  render() {
    if (this.state.hasError) {
      // Friendly "new version available" UI for chunk errors
      if (this.state.isChunkError) {
        return (
          <div className="min-h-[50vh] flex items-center justify-center p-4">
            <div className="max-w-md w-full text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <RefreshCw className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">
                A new version is available
              </h2>
              <p className="text-sm text-muted-foreground">
                The app has been updated. Please refresh to load the latest version.
              </p>
              <Button variant="default" size="sm" onClick={this.handleRefresh} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-[50vh] flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">
              {this.props.fallbackTitle || 'Something went wrong'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" size="sm" onClick={this.handleRetry} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
              <Button variant="default" size="sm" asChild className="gap-2">
                <a href="/dashboard">
                  <Home className="h-4 w-4" />
                  Go to Dashboard
                </a>
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
